import {
  useModalContext,
  WSButton,
  WSButtons,
  WSElement,
  WSFlexBox,
  WSForm,
  WSModal,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { VerificationStatus } from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import React from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYMENTS_STATUS } from "../../../query/payments/keys";
import { usePaymentsStatusQuery } from "../../../query/payments/queries";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../query/users/keys";
import { useMemberProfile, useUserProfile } from "../../../query/users/queries";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import {
  validatorFirstName,
  validatorLastName,
  validatorSSN
} from "../../../utils/validators";

type VerificationUpgradeFormProps = {
  featureName?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
};

type VerificationUpgradeFormValues = {
  firstName: string;
  lastName: string;
  ssn: string;
};

export const useVerificationUpgrade = () => {
  const userId = useUserId();
  const paymentStatusQuery = usePaymentsStatusQuery();
  const memberQuery = useMemberProfile(userId);
  const { openModal } = useModalContext();

  const [requestVerification, requestVerificationMeta] = useWSMutation<
    void,
    AxiosError,
    {
      taxDocumentation?: boolean;
      banking?: boolean;
      cardPayments?: boolean;
    }
  >(
    async (params: {
      taxDocumentation?: boolean;
      banking?: boolean;
      cardPayments?: boolean;
    }) => {
      if (
        (params.banking || params.cardPayments) &&
        !memberQuery.data?.profile.ssnProvided
      ) {
        await new Promise((resolve, reject) => {
          openModal(VERIFICATION_UPGRADE_MODAL, {
            onSubmit: resolve,
            onCancel: () => reject(new Error("No SSN provided"))
          });
        });
      }

      await paymentsService.service.update(userId, {
        verifications: {
          taxDocumentation: params.taxDocumentation
            ? VerificationStatus.Verified
            : undefined,
          banking: params.banking ? VerificationStatus.Verified : undefined,
          cardPayments: params.cardPayments
            ? VerificationStatus.Verified
            : undefined
        }
      });
    },
    {
      dependencies: [QUERY_PAYMENTS_STATUS],
      throwOnError: true
    }
  );

  return {
    isTaxDocumentationRequested:
      paymentStatusQuery.data?.verifications.taxDocumentation !==
      VerificationStatus.None,
    isBankingRequested:
      paymentStatusQuery.data?.verifications.banking !==
      VerificationStatus.None,
    isCardPaymentsRequested:
      paymentStatusQuery.data?.verifications.cardPayments !==
      VerificationStatus.None,

    requestVerification,
    requestVerificationMeta
  };
};

export const VERIFICATION_UPGRADE_MODAL = "VERIFICATION_UPGRADE_MODAL";

export const VerificationUpgradeModal: React.FC = () => {
  return (
    <WSModal name={VERIFICATION_UPGRADE_MODAL} size="S" blockClose>
      {(props: VerificationUpgradeFormProps) => (
        <VerificationUpgradeForm {...props} />
      )}
    </WSModal>
  );
};

export const VerificationUpgradeForm: React.FC<VerificationUpgradeFormProps> = ({
  featureName,
  onSubmit,
  onCancel
}) => {
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const { closeModal } = useModalContext();

  const [submitSsn, submitSsnMeta] = useWSMutation<
    void,
    AxiosError,
    VerificationUpgradeFormValues
  >(
    async ({ ssn }) => {
      await usersService.member.update(userId, {
        memberId: userId,
        profile: {
          ssn
        }
      });
    },
    {
      dependencies: [QUERY_USERS_MEMBER_PROFILE],
      onSuccess: () => {
        closeModal(VERIFICATION_UPGRADE_MODAL);
        onSubmit?.();
      }
    }
  );

  return (
    <WSForm<VerificationUpgradeFormValues>
      defaultValues={{
        firstName: userQuery.data?.profile.firstName,
        lastName: userQuery.data?.profile.lastName
      }}
      validationSchema={Yup.object().shape({
        firstName: validatorFirstName.required("Required"),
        lastName: validatorLastName.required("Required"),
        ssn: validatorSSN.required("Required")
      })}
      onSubmit={submitSsn}
    >
      <WSText.Heading3 mb="M">
        We need some additional information
        {featureName ? ` to enable ${featureName}` : ""}
      </WSText.Heading3>

      <WSFlexBox wrap="nowrap" justify="space-between" mb="M">
        <WSElement style={{ width: "100%" }} mr="S">
          <WSForm.Field
            name="firstName"
            label="Legal first name"
            component={WSTextInput}
          />
        </WSElement>

        <WSElement style={{ width: "100%" }} ml="S">
          <WSForm.Field
            name="lastName"
            label="Legal last name"
            component={WSTextInput}
          />
        </WSElement>
      </WSFlexBox>

      <WSForm.Field
        name="ssn"
        label="SSN"
        component={WSTextInput}
        componentProps={{
          type: "password",
          allowToShowPassword: true,
          formatOptions: {
            numericOnly: true,
            blocks: [9]
          }
        }}
        mb="M"
      />

      <WSErrorMessage mb="M" contextKey="PaymentsVerificationUpgrade" />

      <WSButtons forceFullWidth>
        <WSButton.Secondary
          name="cancel"
          onClick={() => {
            closeModal(VERIFICATION_UPGRADE_MODAL);
            onCancel?.();
          }}
        >
          Cancel
        </WSButton.Secondary>
        <WSButton name="continue" loading={submitSsnMeta.isLoading}>
          Continue
        </WSButton>
      </WSButtons>
    </WSForm>
  );
};
